/**
 * GvG Planner domain types — board primitives.
 *
 * The board is organized as a list of top-level "rooms" (seeded with Attack,
 * Defense, and Pool). Each room contains a configurable list of named groups
 * of players; players not yet placed into a specific group live in the `Pool`
 * room (id `'pool'`, `noPlayerLimit: true`), which ships with a single seeded
 * group labelled `Bench`. A player belongs to exactly one group at a time.
 *
 * Group and room ids are plain strings so the planner can dynamically add and
 * remove rooms and groups beyond the seeded A1..A3 / D1..D3 / P1 set. The
 * runtime data shape is the canonical JSON structure consumed by the planner.
 *
 * Note on `Weapon.color`: the field is intentionally redundant with
 * `roleToColor(weapon.role)` — both are kept in sync at construction time
 * (every catalog entry in `src/data/weapons.ts` sets `color: roleToColor(role)`
 * by hand). We keep the field instead of deriving it at read time so
 * consumers (e.g. `PlayerCard`'s path strips) can do a single property
 * lookup without pulling in `roleToColor`. If you add a new role or
 * catalog entry, set both fields; the `WeaponRoleColor → 'role-*'` mapping
 * in `WeaponRoleColor` is the single source of truth.
 */

import type { PlayerRole } from '@/types/weapon'

/**
 * Stable id for a board room. Seeded as `'attack' | 'defense' | 'pool'`; user-created rooms get a `room-<n>` suffix.
 *
 * NOTE: `RoomId`, `GroupId`, and `PlayerId` are *nominal* aliases for
 * `string` — not branded/opaque types. They exist purely as a readability
 * signal at the type level (e.g. so `setMainWeapon(playerId, …)` is
 * visually distinct from `setMainWeapon(groupId, …)`). They do NOT enforce
 * uniqueness or shape at runtime; collisions or wrong-string-passes are
 * caught by the type guards in the store, not by the compiler. If true
 * branding is required, convert these to `string & { readonly __brand: … }`.
 */
export type RoomId = string
export type GroupId = string
export type PlayerId = string
/** Position row inside a room: top lane / mid lane / bot lane. */
export const GroupPosition = {
  Top: 'top',
  Mid: 'mid',
  Bot: 'bot',
} as const

export type GroupPosition = (typeof GroupPosition)[keyof typeof GroupPosition]

/** Visual accent hue applied to a room header. Cycled through red → blue → green → purple when new rooms are added. */
export const RoomAccent = {
  Red: 'red',
  Blue: 'blue',
  Green: 'green',
  Purple: 'purple',
} as const

export type RoomAccent = (typeof RoomAccent)[keyof typeof RoomAccent]

/**
 * Color a tag chip renders with on `PlayerCard` and in the toolbar filter
 * strip. The string values MUST round-trip with the backend `tag_color`
 * Postgres enum (`apps/backend/shared/entity/src/sea_orm_active_enums.rs`)
 * — see the `Object.keys(TagColor).length === 10` pin in
 * `playerTags.test.ts`. An unknown value from the server is tolerated
 * (mapped to `Neutral` at the type boundary) so a future server-side
 * addition doesn't break old clients.
 */
export const TagColor = {
  Neutral: 'Neutral',
  Red: 'Red',
  Blue: 'Blue',
  Green: 'Green',
  Purple: 'Purple',
  Amber: 'Amber',
  Rose: 'Rose',
  Cyan: 'Cyan',
  Slate: 'Slate',
  Stone: 'Stone',
} as const

export type TagColor = (typeof TagColor)[keyof typeof TagColor]

/**
 * A tag defined in a guild's catalog. Officers create these via the
 * `playerTagCreate` mutation (plan 05). The id is the backend DB PK;
 * the chip strip on `PlayerCard` and the toolbar Tags filter render
 * `name` with a `bg-tag-<color>` tinted background.
 */
export interface PlayerTag {
  readonly id: string
  readonly guildId: string
  readonly name: string
  /** Stable system key for seeded tactical tags; user-created tags have none. */
  readonly seedKey: string | null
  readonly color: TagColor
}

/**
 * One row of the guild library (`guild_builds`) surfaced inside the FX-05
 * builds popup on `PlayerCard`. The backend returns the full library per
 * player — every `guild_build` row for the player's guild — plus an
 * `isAssigned` flag the popup reads to badge "assigned to me" rows.
 * The `id` is the backend `guild_builds.id` PK and is what assign / unassign
 * / set-active mutations key off (never an array index).
 */
export interface GuildBuildEntry {
  readonly id: string
  readonly orderIndex: number
  readonly weaponSlugs: ReadonlyArray<string>
  readonly isAssigned: boolean
}

export interface Player {
  readonly id: PlayerId
  readonly discordUserId: string
  /** True once this Discord identity has authenticated with the planner. */
  readonly hasSignedIn: boolean
  /** Compatibility alias for `guildNickname`. */
  readonly discordName: string
  /** Discord server-specific nickname, with player/account fallback. */
  readonly guildNickname: string
  /** Discord global display name, with account fallback. */
  readonly playerNickname: string
  /** Discord unique account username. */
  readonly playerAccountName: string
  readonly inGameName: string
  readonly role: PlayerRole
  /**
   * Group the player currently belongs to. Invariant: a non-deleted player
   * should always resolve to a group that exists in `BoardState.groups`
   * (the Pool's default bucket is the landing pad for `addNewPlayer`).
   * `null` is reserved for two transient windows:
   *   * Brand-new rows from `makeNewPlayer` before `addNewPlayer` lands
   *     them in the Pool.
   *   * Players whose group was just deleted and the Pool room isn't
   *     available to absorb them (see `removeGroup`). The store's
   *     getPoolDefaultGroupId() helper resolves the safe fallback; the UI
   *     never renders an orphan row because the Pool can't actually be
   *     removed.
   * Persistence always stores a resolved `GroupId`; `null` only exists
   * mid-action.
   */
  readonly group: GroupId | null
  /** Primary weapon slugs — 1 or 2 entries. */
  readonly mainWeapons: ReadonlyArray<string>
  /** Alternate weapon build — empty, or exactly a pair of slugs. */
  readonly altWeapons: ReadonlyArray<string>
  /** Saved extra builds. Each entry is exactly two distinct weapon slugs. */
  readonly builds: ReadonlyArray<ReadonlyArray<string>>
  /**
   * Guild library builds visible in this player's popup. Each row carries the
   * full guild library (every `guild_build` for the player's guild) plus an
   * `isAssigned` flag the FX-05 popup reads to badge "assigned to me" rows.
   * The `id` is the backend `guild_builds.id` PK — set-active / assign /
   * unassign all key off it, never an array index.
   */
  readonly guildBuilds: ReadonlyArray<GuildBuildEntry>
  /** Which `builds` slot is currently active. -1 means "no saved build (use mainWeapons directly)". */
  readonly activeBuildIndex: number
  /** Which `guildBuilds` slot is currently active. -1 means "no guild build is active". Server-authoritative FK. */
  readonly activeGuildBuildIndex: number
  /**
   * Tags attached to this player (plan 05). Populated by the `board` query
   * (LEFT JOIN via `player_tag_assignments` + `player_tags`). Always
   * non-null; empty array when the player carries no tags. The chip strip
   * on `PlayerCard` reads this directly; the toolbar filter uses it to
   * decide whether to dim a row.
   */
  readonly tags: ReadonlyArray<PlayerTag>
}

export interface PlayerGroup {
  readonly id: GroupId
  readonly label: string
  /** Lane this group occupies within its room. */
  readonly position: GroupPosition
  /** Ordered list of player ids currently assigned to this group. */
  readonly playerIds: ReadonlyArray<PlayerId>
  /** Target roster size for this group (clamped between 1 and 10). Ignored when the parent room has `noPlayerLimit: true`. */
  readonly maxPlayers: number
  /** Shared server-persisted presentation state. When true, the planner renders only the group header. */
  readonly collapsed: boolean
}

export interface Room {
  readonly id: RoomId
  readonly name: string
  readonly groupIds: ReadonlyArray<GroupId>
  /** Visual accent used by the room header. Cycles through distinct hues for newly added rooms. */
  readonly accent: RoomAccent
  /**
   * When true, groups inside this room ignore their `maxPlayers` cap and the
   * "Over capacity" warning is suppressed. Used by the `Pool` room to host
   * benched players without a roster cap.
   */
  readonly noPlayerLimit: boolean
}

/** Board display mode. Toggled by the user from the view toolbar; persisted in `BoardState.viewMode`. */
export const ViewMode = {
  Detail: 'detail',
  Compact: 'compact',
} as const

export type ViewMode = (typeof ViewMode)[keyof typeof ViewMode]

/** Which Discord identity is presented on planner player cards. */
export const PlayerNameMode = {
  GuildNickname: 'guildNickname',
  PlayerNickname: 'playerNickname',
  PlayerAccountName: 'playerAccountName',
} as const

export type PlayerNameMode = (typeof PlayerNameMode)[keyof typeof PlayerNameMode]

export interface BoardState {
  readonly players: ReadonlyArray<Player>
  readonly groups: ReadonlyArray<PlayerGroup>
  readonly rooms: ReadonlyArray<Room>
  readonly viewMode: ViewMode
  readonly playerNameMode: PlayerNameMode
}

/**
 * Migration surface — `BoardState` only models the current schema, but
 * `useLocalStorage` may still hand us older blobs. The store handles two
 * legacy shapes at load time:
 *
 *   1. `divisions: ReadonlyArray<Division>` in place of `rooms`. Lifted to
 *      `rooms` (each division carrying the same shape, with `noPlayerLimit`
 *      defaulting to `false`).
 *   2. A `pool` field — either a flat `ReadonlyArray<PlayerId>` (v1
 *      bucket-less form) or `{ reserve, declined }` (the bucket-era form).
 *      Any ids found there are re-homed into the Pool room's default bucket
 *      (`P2` Signed) and the `pool` field is dropped.
 *
 * In addition, the store folds a legacy `P1` ("Bench") bucket inside the
 * Pool into `P2` (Signed), and coerces any `role` value that is no longer
 * in `WeaponRole` to `WeaponRole.Dps`. See `gvgPlannerStore.ts` for the
 * full migration pipeline. The legacy fields are intentionally NOT
 * re-declared on `BoardState` — they live only as `unknown` keys in the
 * raw persisted JSON.
 */
